import { useCallback } from 'react';
import { useChatStore } from '../store';
import type { ChatMessage, ToolActivity, ChatChartData } from '../store';
import { useAuthStore } from '../store';
import {
  getChatConversations,
  createChatConversation,
  getChatConversation,
  addChatMessage,
  deleteChatConversation,
} from '../utils/api';

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  get_stock_sentiment: 'Checking sentiment',
  get_top_opportunities: 'Finding buy opportunities',
  get_sell_signals: 'Finding sell signals',
  get_sentiment_history: 'Loading sentiment history',
  search_stocks: 'Searching stocks',
  get_market_overview: 'Getting market overview',
  get_price_history: 'Loading price history',
  get_fundamentals: 'Loading fundamentals',
  get_earnings_history: 'Loading earnings',
  get_income_statement: 'Loading income statement',
  get_cash_flow: 'Loading cash flow',
  av_quote: 'Getting real-time quote',
  av_company_overview: 'Loading company profile',
  av_technical_sma: 'Calculating SMA',
  av_technical_ema: 'Calculating EMA',
  av_technical_rsi: 'Calculating RSI',
  av_technical_macd: 'Calculating MACD',
  av_technical_bbands: 'Calculating Bollinger Bands',
  av_technical_stoch: 'Calculating Stochastic',
  av_technical_adx: 'Calculating ADX',
  av_technical_vwap: 'Calculating VWAP',
  av_technical_atr: 'Calculating ATR',
  av_economic_cpi: 'Loading CPI data',
  av_economic_fed_rate: 'Loading Fed rate',
  av_economic_unemployment: 'Loading unemployment data',
  av_economic_gdp: 'Loading GDP data',
  av_economic_treasury: 'Loading treasury yields',
  av_news_sentiment: 'Analyzing news sentiment',
  av_market_movers: 'Loading market movers',
  render_chart: 'Generating chart',
};

function getToolDisplayName(name: string, args?: Record<string, any>): string {
  const base = TOOL_DISPLAY_NAMES[name] || name;
  const ticker = args?.ticker || args?.symbol || args?.tickers;
  return ticker ? `${base} for ${String(ticker).toUpperCase()}` : base;
}

export function useChat() {
  const { token, isAuthenticated } = useAuthStore();
  const {
    conversations,
    activeConversationId,
    messages,
    isLoading,
    isOpen,
    setConversations,
    setActiveConversation,
    setMessages,
    addMessage,
    updateMessage,
    addToolActivity,
    addChartData,
    setLoading,
    setOpen,
    clearMessages,
  } = useChatStore();

  // Load conversations on mount
  const loadConversations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getChatConversations();
      setConversations(data.conversations.map(c => ({
        id: c.id,
        title: c.title,
        created_at: c.created_at,
        updated_at: c.updated_at,
        messages: []
      })));
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  }, [isAuthenticated, setConversations]);

  // Load a specific conversation
  const loadConversation = useCallback(async (conversationId: number) => {
    if (!isAuthenticated) return;
    try {
      const data = await getChatConversation(conversationId);
      const chatMessages: ChatMessage[] = (data.messages || []).map(m => ({
        id: m.id.toString(),
        role: m.role as 'user' | 'assistant',
        content: m.content,
        timestamp: new Date(m.created_at)
      }));
      setMessages(chatMessages);
      setActiveConversation(conversationId);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  }, [isAuthenticated, setMessages, setActiveConversation]);

  // Create new conversation
  const createNewConversation = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      const data = await createChatConversation();
      await loadConversations();
      setActiveConversation(data.id);
      setMessages([]);
      return data.id;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      return null;
    }
  }, [isAuthenticated, loadConversations, setActiveConversation, setMessages]);

  // Delete conversation
  const deleteConversation = useCallback(async (conversationId: number) => {
    if (!isAuthenticated) return;
    try {
      await deleteChatConversation(conversationId);
      if (activeConversationId === conversationId) {
        clearMessages();
      }
      await loadConversations();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  }, [isAuthenticated, activeConversationId, clearMessages, loadConversations]);

  // Send message
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    let conversationId = activeConversationId;

    // Create new conversation if needed
    if (!conversationId && isAuthenticated) {
      conversationId = await createNewConversation();
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
    };

    addMessage(userMessage);
    setLoading(true);

    // Save user message to backend
    if (conversationId && isAuthenticated) {
      try {
        await addChatMessage(conversationId, 'user', text.trim());
      } catch (error) {
        console.error('Failed to save user message:', error);
      }
    }

    // Create placeholder for assistant message
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    addMessage(assistantMessage);

    try {
      // Use the API base URL for production, or relative path for local dev
      const apiBase = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiBase}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          messages: [...messages, userMessage].map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      if (!response.ok) throw new Error('Chat request failed');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error('No response body');

      let fullContent = '';
      let buffer = ''; // Buffer for incomplete chunks

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Append new data to buffer
        buffer += decoder.decode(value, { stream: true });

        // Split by double newline (SSE event separator)
        const events = buffer.split('\n\n');

        // Keep the last potentially incomplete event in buffer
        buffer = events.pop() || '';

        for (const event of events) {
          const lines = event.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.tool_status) {
                  const ts = data.tool_status;
                  const activity: ToolActivity = {
                    name: ts.name,
                    arguments: ts.arguments,
                    status: ts.status,
                    displayName: getToolDisplayName(ts.name, ts.arguments),
                  };
                  addToolActivity(assistantMessageId, activity);
                } else if (data.chart) {
                  const chart: ChatChartData = {
                    chartType: data.chart.chartType,
                    title: data.chart.title,
                    symbol: data.chart.symbol || '',
                    data: data.chart.data,
                    ...(data.chart.quarterlyData && { quarterlyData: data.chart.quarterlyData }),
                    ...(data.chart.balanceSheetData && { balanceSheetData: data.chart.balanceSheetData }),
                  };
                  addChartData(assistantMessageId, chart);
                } else if (data.content) {
                  fullContent += data.content;
                  updateMessage(assistantMessageId, fullContent);
                } else if (data.error) {
                  updateMessage(assistantMessageId, `Error: ${data.error}`);
                }
              } catch {
                // Ignore parse errors for incomplete JSON
              }
            }
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        const lines = buffer.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.tool_status) {
                const ts = data.tool_status;
                addToolActivity(assistantMessageId, {
                  name: ts.name,
                  arguments: ts.arguments,
                  status: ts.status,
                  displayName: getToolDisplayName(ts.name, ts.arguments),
                });
              } else if (data.chart) {
                addChartData(assistantMessageId, {
                  chartType: data.chart.chartType,
                  title: data.chart.title,
                  symbol: data.chart.symbol || '',
                  data: data.chart.data,
                  ...(data.chart.quarterlyData && { quarterlyData: data.chart.quarterlyData }),
                  ...(data.chart.balanceSheetData && { balanceSheetData: data.chart.balanceSheetData }),
                });
              } else if (data.content) {
                fullContent += data.content;
                updateMessage(assistantMessageId, fullContent);
              }
            } catch {
              // Ignore parse errors
            }
          }
        }
      }

      // Save assistant message to backend
      if (conversationId && isAuthenticated && fullContent) {
        try {
          await addChatMessage(conversationId, 'assistant', fullContent);
          // Refresh conversations to update titles
          await loadConversations();
        } catch (error) {
          console.error('Failed to save assistant message:', error);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      updateMessage(assistantMessageId, 'Sorry, I encountered an error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [
    isLoading,
    activeConversationId,
    isAuthenticated,
    token,
    messages,
    addMessage,
    updateMessage,
    addToolActivity,
    addChartData,
    setLoading,
    createNewConversation,
    loadConversations
  ]);

  // Start new chat
  const startNewChat = useCallback(() => {
    clearMessages();
  }, [clearMessages]);

  return {
    // State
    conversations,
    activeConversationId,
    messages,
    isLoading,
    isOpen,

    // Actions
    loadConversations,
    loadConversation,
    createNewConversation,
    deleteConversation,
    sendMessage,
    startNewChat,
    setOpen,
    clearMessages,
  };
}
